"use client";
import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import Link from "next/link";
import Image from "next/image";

// Define the BlogPost interface based on desired output
interface BlogPost {
  id: number;
  title: { rendered: string };
  excerpt: { rendered: string };
  link: string;
  date: string;
  featured_image_url: string;
  author_name: string;
  category_name: string;
  reading_time: string;
}

// Define the RawPost interface to type the API response
interface RawPost {
  id: number;
  date: string;
  title: { rendered: string };
  excerpt: { rendered: string };
  content: { rendered: string };
  link: string;
  _embedded: {
    author?: { name: string }[];
    "wp:featuredmedia"?: { source_url: string }[];
    "wp:term"?: { name: string }[][];
  };
}

const BlogsSection: React.FC = () => {
  const [items, setItems] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const rawSlug = "storeblog";

  useEffect(() => {
    let isMounted = true;

    const fetchBlogs = async () => {
      try {
        const res = await fetch('/api/blog', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rawSlug }),
        });

        if (!res.ok) throw new Error(`Failed to fetch blogs: ${res.statusText}`);

        const { articles }: { articles: RawPost[] } = await res.json();

        if (!Array.isArray(articles) || !articles.length) return;

        const formattedPosts: BlogPost[] = articles.map(post => {
          const contentText = post.content?.rendered?.replace(/<[^>]+>/g, "") || "";
          const wordCount = contentText.trim().split(/\s+/).length;
          const readingTime = `${Math.max(1, Math.ceil(wordCount / 200))} min read`;

          return {
            id: post.id,
            title: post.title,
            excerpt: post.excerpt,
            link: post.link,
            date: new Date(post.date).toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric",
            }),
            featured_image_url:
              post._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
              "https://placehold.co/420x200?text=No+Image",
            author_name: post._embedded?.author?.[0]?.name || "Unknown Author",
            category_name: post._embedded?.["wp:term"]?.[0]?.[0]?.name || "Uncategorized",
            reading_time: readingTime,
          };
        });

        if (isMounted) setItems(formattedPosts);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchBlogs();

    return () => {
      isMounted = false; // Cleanup to avoid setting state on unmounted component
    };
  }, [rawSlug]);

  return (
    <section id="blogsection" className="container-lg py-5 text-white">
      <div className="Haddings">
        <h4 className="fw-bold display-5 text-center textHad">Latest from our Blogs</h4>
      </div>
      <div className="row g-4 mt-0">
        {loading ? (
          <p>Loading blog posts...</p>
        ) : items && items.length > 0 ? (
          <Swiper
            className="mt-4 px-4"
            modules={[Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            autoplay={{ delay: 5000 }}
            breakpoints={{
              576: {
                slidesPerView: 3,
              },
              768: {
                slidesPerView: 2,
              },
              1024: {
                slidesPerView: 4,
              },
            }}
          >
            {items.map((item, i) => (
              <SwiperSlide key={i} className="py-3">
                <div className="col-12 col-md-12">
                <div className="blogBord hvr-sink">
                  <div className="d-flex justify-content-between">
                    <p className="m-0 small blogDate fw-bold py-2 px-3 rounded-pill mb-3  ">
                      <span className="">{item.date}</span>
                    </p>
                  </div>
                  <Link href={item.link} className="blog-title" target="_blank" rel="noopener noreferrer">
                    <Image
                      width={420}
                      height={200}
                      src={item.featured_image_url}
                      className="blog-thumbnail rounded-4 img-fluid"
                      alt={`Post ${i}`}
                    />
                  </Link>

                  <h4 className="textOrange fs-5 fw-semibold my-lg-4 my-md-3 my-3">
                    <Link href={item.link} className="blog-title text-decoration-none textOrange" target="_blank" rel="noopener noreferrer">
                      {item.title.rendered}
                    </Link>
                  </h4>
                  <p
                    className="mt-2 textPara fs-6 fw-600 line-clamp"
                    dangerouslySetInnerHTML={{ __html: item.excerpt.rendered }}
                  ></p>
                  <div className="mt-2">
                    <p className="text-muted small my-2">
                      Posted in <strong dangerouslySetInnerHTML={{ __html: item.category_name }}></strong>
                    </p>
                  </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        ): (
          <h6 className="display-7 text-center textHad">No blog posts found.</h6>
        )}
      </div>
    </section>
  );
};

export default BlogsSection;
